import { FormattedMessage, useIntl } from 'react-intl';
import { useMemo, useState } from 'react';
import {
  DepartmentAccessLevelDTO,
  useGetUsersForDepartment,
} from '@/api/trafikportalen';
import { Loading } from '@/components/Loading';
import { DataNotFound } from '@/components/DataNotFound';
import { IconPlus } from '@/icons';
import Button from '@/components/core/Button';
import { DepartmentAdminsListItem } from './DepartmentAdminsListItem';
import { AddDepartmentAdminDialog } from './AddDepartmentAdminDialog';
import { DialogWindow } from '@/components/Dialog';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { ExtendedUserDTO } from '@/hooks/useGetAllAdmins';
import { useAuth } from '@/stores/auth';
import { AdminType } from '@/types/administrator';

const departmentAdminType: Record<DepartmentAccessLevelDTO, AdminType> = {
  none: 'deptReader',
  viewer: 'deptReader',
  'bearer-manager': 'deptBearerManager',
  editor: 'deptAdmin',
};

interface Props {
  departmentId: string;
}

export const DepartmentAdmins: React.FC<Props> = ({ departmentId }) => {
  const { formatMessage } = useIntl();
  const { userProfile } = useAuth();

  const {
    data: admins,
    isLoading,
    isError,
  } = useGetUsersForDepartment(departmentId);

  const updatedAdmins: ExtendedUserDTO[] = useMemo(() => {
    if (!admins) return [];

    return admins.map(
      (admin): ExtendedUserDTO => ({
        ...admin,
        adminType: departmentAdminType[admin.access_level],
        departments: [
          { department_id: departmentId, access_level: admin.access_level },
        ],
      }),
    );
  }, [admins, departmentId]);

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Kunde inte ladda administratörer" />
        }
      />
    );
  }

  return (
    <div className="flex flex-col py-4 @container md:py-8">
      <div className="mb-4 flex w-full flex-row flex-wrap justify-between">
        <h2>
          <FormattedMessage defaultMessage="Administratörer" />
        </h2>
        {hasDepartmentWriteAccess(departmentId) && (
          <Button
            startIcon={<IconPlus />}
            onClick={() => setIsInviteDialogOpen(true)}
          >
            <FormattedMessage defaultMessage="Lägg till administratör" />
          </Button>
        )}
      </div>

      {updatedAdmins.length > 0 ? (
        <ul className="flex w-full flex-col gap-2">
          {updatedAdmins.map((admin) => (
            <li
              key={admin.id}
              className="flex w-full max-w-full flex-auto items-center justify-between gap-1"
            >
              <DepartmentAdminsListItem
                administrator={admin}
                className="min-w-0 flex-1"
                currentUserEmail={userProfile?.email}
              />
            </li>
          ))}
        </ul>
      ) : (
        <span className="italic text-text-700">
          <FormattedMessage defaultMessage="Inga administratörer" />
        </span>
      )}

      <DialogWindow
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        title={formatMessage({
          defaultMessage: 'Lägg till avdelningsadministratör',
        })}
        content={
          <AddDepartmentAdminDialog
            departmentId={departmentId}
            onClose={() => setIsInviteDialogOpen(false)}
          />
        }
      />
    </div>
  );
};
